import "#/legacy/namespaces.js";

/**
 * @file Backbone finder results step and legacy search writer for `sp-finder`.
 * The doctor finder creates `WizardStep2` as its results page.
 * On collection filter changes, the writer passes `generateFetchParams({ onlyFilters: true })`
 * and the collection's filter values through the legacy adapter to the enclosing wrapper's state and subscribers.
 * Removing the view disconnects the adapter and its filter change listeners.
 * This module exists while the Backbone finder exists and is deleted with it in
 * https://github.com/combinaut/sparkle/issues/31058 — [Sparkle] Replace the Backbone finder with sp-finder.
 */
import { normalizeMapBounds } from "#ce/components/SpFinder/engine/map-bounds";
import { cloneFinderData } from "#ce/components/SpFinder/engine/finder-data";
import { FINDER_FILTER_TAG } from "#ce/components/SpFinder/engine/finder-filter-protocol";
import { connectLegacyFinder } from "#ce/apps/SpFinder/main";
import "#legacy/utilities/params.js";
import "#legacy/utilities/iso_date.js";
import "#legacy/utilities/sticky.js";
import debounce from "lodash/debounce";
import "#legacy/utilities/set_input.js";
import "#legacy/application/responsive/index.js";
import "#legacy/shared/events.js";
import "#legacy/shared/components/modal.js";
import "#legacy/shared/components/video_modal.js";
import "#legacy/shared/components/sparkle_loader.js";
import "./autocomplete.js";
import "./wizard_step.js";
import "#finder-legacy/physician_finder/helpers/filter_app_readiness.js";
import "#finder-legacy/physician_finder/helpers/filter_app_value.js";
import "#finder-legacy/physician_finder/helpers/filter_focus.js";
import "#finder-legacy/physician_finder/helpers/filter_prerequisite.js";
import.meta.glob("#finder-legacy/physician_finder/views/wizard_step_2/*.{js,hbs,jst.ejs}", { eager: true });

const FILTER_SUMMARY_LINK_SELECTOR = '[data-filter-summary-behaviour="filter-link"]';
const SEARCH_FILTER_SELECTOR = '[data-behaviour~="search-filter"]';
const SORT_TAG = "sp-sort";
const RESULTS_OPTION_SELECTOR = '[data-behaviour~="results-option"]';
const PROVIDER_SEARCH_CONTEXT_PARAM = "provider_search_context";
const SEARCH_WHO_WORKS_HERE_CONTEXT = "search_who_works_here";
const DEFAULT_MAX_SLOTS = 3;
const MOBILE_MAX_SLOTS = undefined; // Scroll horizontally
// Handle sequential but synchronous updates to the filters by queuing them until synchronous execution is complete
// then apply them in a single call. This avoids adding several history states in the browser and eliminates unwanted
// intermediate requests to the server.
let updateFiltersQueue = new Map();
let resetPageQueue = false;

// Preserve subclasses attached by eager glob imports before replacing `DoctorFinder.Views.WizardStep2`.
const __previousWS2 = DoctorFinder.Views.WizardStep2;

DoctorFinder.Views.WizardStep2 = class WizardStep2 extends DoctorFinder.Views.WizardStep {
  static {
    this.prototype.template = false;
    this.prototype.id = "wizard-step-2";

    this.prototype.events = {
      [`change ${SEARCH_FILTER_SELECTOR}`]: "handleSearchFilterChange",
      [`change ${RESULTS_OPTION_SELECTOR}[name='view']`]: "handleViewOptionChange",
      'click [data-doctor-finder-behaviour~="show-map-view"]': "showMapView",
      "click .search-filters-skill": "handleSkillClick",
      "click [data-doctor-finder-behaviour~=show-page]:not(.disabled)": "handleShowPage",
      "click [data-doctor-finder-behaviour~=set-per-page]:not(.disabled)": "handleSetPerPage",
      "click [data-doctor-finder-behaviour~=add-filters]": "handleAddFiltersClick",
      "click [data-doctor-finder-behaviour~=reset-filters]": "handleResetFiltersClick",
      "click [data-link-behaviour~=add-filters]": "handleAddFiltersClick",
      "click [data-link-behaviour~=focus-filter]": "handleFocusFilterClick",
      "click [data-doctor-finder-behaviour=navigate-to-main]": "handleNavigateToMainClick",
    };

    this.prototype.tooltips = {
      ".appointment-filter-view-option": { placement: "bottom" },
      ".appointment-grid a": {},
    };
  }

  constructor() {
    super(...arguments);
  }

  initialize(options = {}) {
    this.initialize = this.initialize.bind(this);
    this.handleFiltersChange = this.handleFiltersChange.bind(this);
    this.handleRequestStarted = this.handleRequestStarted.bind(this);
    this.handleRequestCompleted = this.handleRequestCompleted.bind(this);
    this.handleRequestFailed = this.handleRequestFailed.bind(this);
    this.handleViewChanged = this.handleViewChanged.bind(this);
    this.handleMobileBreakpointEnter = this.handleMobileBreakpointEnter.bind(this);
    this.handleMobileBreakpointExit = this.handleMobileBreakpointExit.bind(this);
    this.handleRemoveFilterClick = this.handleRemoveFilterClick.bind(this);
    this.handleViewOptionChange = this.handleViewOptionChange.bind(this);
    this.handleSearchFilterChange = this.handleSearchFilterChange.bind(this);
    this.handleSkillClick = this.handleSkillClick.bind(this);
    this.handleShowPage = this.handleShowPage.bind(this);
    this.handleSetPerPage = this.handleSetPerPage.bind(this);
    this.handleResultAddFilters = this.handleResultAddFilters.bind(this);
    this.handleAddFiltersClick = this.handleAddFiltersClick.bind(this);
    this.handleFocusFilterClick = this.handleFocusFilterClick.bind(this);
    this.handleResetFiltersClick = this.handleResetFiltersClick.bind(this);
    this.nearFilterChanges = this.nearFilterChanges.bind(this);
    this.persistLocation = this.persistLocation.bind(this);
    this.clearPersistedLocation = this.clearPersistedLocation.bind(this);
    this.updateStickyMapOffset = this.updateStickyMapOffset.bind(this);
    this.handleNavigateToMainClick = this.handleNavigateToMainClick.bind(this);
    this.handleCollectionUpdate = this.handleCollectionUpdate.bind(this);
    this.handleMapBoundsScopingModeChange = this.handleMapBoundsScopingModeChange.bind(this);
    this.render = this.render.bind(this);
    this.selectedVisitTypeText = this.selectedVisitTypeText.bind(this);
    this.isDirectoryLayout = this.isDirectoryLayout.bind(this);
    this.shouldScopeToMapBoundsByDefault = this.shouldScopeToMapBoundsByDefault.bind(this);
    this.storeDoctorFinderMapBoundsScopingMode = this.storeDoctorFinderMapBoundsScopingMode.bind(this);
    this.loadDoctorFinderMapBoundsScopingMode = this.loadDoctorFinderMapBoundsScopingMode.bind(this);
    this.decorateFilters = this.decorateFilters.bind(this);
    this.decorateFilterInputsWithFilterDescription = this.decorateFilterInputsWithFilterDescription.bind(this);
    this.getSerializedInputData = this.getSerializedInputData.bind(this);
    this.updateSearchHeaderVisibility = this.updateSearchHeaderVisibility.bind(this);
    this.updateSearchSummaryVisibility = this.updateSearchSummaryVisibility.bind(this);
    this.updateFiltersVisibility = this.updateFiltersVisibility.bind(this);
    this.updateViewInput = this.updateViewInput.bind(this);
    this.updateInputsFromJSON = this.updateInputsFromJSON.bind(this);
    this.setInputsFromJSON = this.setInputsFromJSON.bind(this);
    this.showMapView = this.showMapView.bind(this);
    this.navigateToDate = this.navigateToDate.bind(this);
    this.requiredFilterNames = this.requiredFilterNames.bind(this);
    this.removeFilters = this.removeFilters.bind(this);
    this.addFilters = this.addFilters.bind(this);
    this.updateFiltersWithQueue = this.updateFiltersWithQueue.bind(this);
    this.getFilterNames = this.getFilterNames.bind(this);
    this.updateFilterCount = this.updateFilterCount.bind(this);
    this.setStickyHeader = this.setStickyHeader.bind(this);
    this.updateHeaderTitle = this.updateHeaderTitle.bind(this);
    this.generateHeaderTitle = this.generateHeaderTitle.bind(this);
    this.getTitleConfig = this.getTitleConfig.bind(this);
    this.defaultTitle = this.defaultTitle.bind(this);
    this.focusInvalidFilters = this.focusInvalidFilters.bind(this);
    this.updateConditionalFilters = this.updateConditionalFilters.bind(this);
    this.getScopingFilters = this.getScopingFilters.bind(this);
    this.launchVideoModal = this.launchVideoModal.bind(this);
    this.updateErrors = this.updateErrors.bind(this);
    this.updateUnoccluder = this.updateUnoccluder.bind(this);
    this.changePage = this.changePage.bind(this);
    this.setPage = this.setPage.bind(this);
    this.setPerPage = this.setPerPage.bind(this);
    this.loadingState = this.loadingState.bind(this);
    this.readyState = this.readyState.bind(this);
    this.showLoadingIndicators = this.showLoadingIndicators.bind(this);
    this.hideLoadingIndicators = this.hideLoadingIndicators.bind(this);
    this.addLoadingIndicator = this.addLoadingIndicator.bind(this);
    this.availabilityIndicatorParams = this.availabilityIndicatorParams.bind(this);
    this.scrollToTopOfResults = this.scrollToTopOfResults.bind(this);
    this.mapStickyOffset = this.mapStickyOffset.bind(this);
    this.nextTick = this.nextTick.bind(this);
    this.sortingByDistanceWithoutZip = this.sortingByDistanceWithoutZip.bind(this);
    this.focusFilter = this.focusFilter.bind(this);
    this.focusFilterGroup = this.focusFilterGroup.bind(this);
    this.remove = this.remove.bind(this);
    this.ownedFilterNames = this.ownedFilterNames.bind(this);
    this.multiValueFilterApps = this.multiValueFilterApps.bind(this);
    this.restoreMultiValueFilterApp = this.restoreMultiValueFilterApp.bind(this);
    this.flushPendingFilterRestore = this.flushPendingFilterRestore.bind(this);
    this.applyInputParams = this.applyInputParams.bind(this);

    let occlusionOptions;

    super.initialize(options);

    DoctorFinder.Helpers.MAX_SLOTS = DEFAULT_MAX_SLOTS;

    // `visit_types` belongs to the standalone Visit Type control wherever the embed keeps that
    // control, and to the availability filter otherwise. `DoctorFinderEmbed#visit_type_filter_owns_param?`
    // decides it, and the omnisearch store reads the same value.
    this.availabilityWidgetOwnsVisitTypes = !this.options.visitTypeFilterOwnsParam;

    // TODO: When we render filters internally we'll need to pass this in as an option or detect the defaults per-publishing domain,
    //       but for now since they are rendered server-side we detect which are rendered and that is our default setting.
    this.availableFilterInputs = this.$(SEARCH_FILTER_SELECTOR);
    this.availableOptionInputs = this.$(RESULTS_OPTION_SELECTOR);
    this.availableInputs = this.availableOptionInputs.add(this.availableFilterInputs);
    // A multi-value filter app (the Provider panel) owns several params; it
    // advertises them in server-rendered `data-filter-names` so the registry is
    // correct even before the custom element upgrades (see `ownedFilterNames`).
    // jQuery.map flattens the returned arrays in; single-value filters yield a
    // one-element array from their `name` attribute.
    this.availableFilters = _.uniq(this.availableFilterInputs.map((i, el) => this.ownedFilterNames(el)).get());
    this.availableFilterControls = this.$('[data-doctor-finder-behaviour="filter-control"]');
    this.availableResultsControls = this.$('[data-doctor-finder-behaviour="results-control"]');

    // The skill (care) and name (provider) facets are <sp-finder-filter> elements
    // backed by the shared omnisearch store; they apply their filters through the
    // standard `search-filter` bridge like every other in-bar facet.

    this.mapBoundsMode = this.shouldScopeToMapBoundsByDefault() ? "auto" : "manual";

    this.searchResultsView = new DoctorFinder.Views.SearchResults({
      id: "doctor-finder-search-results",
      collection: this.collection,
      stickyHeaderOffset: this.mapStickyOffset,
      doctorMessages: this.options.doctorMessages,
      i18n: this.I18n,
      hideMapView: this.options.hideMapView,
      listLayout: this.options.listLayout,
      maxResults: this.options.maxResults,
      resultConfig: _.extend({}, this.options.result, { resultTemplate: this.options.resultTemplate }),
      pagination: this.options.pagination,
      resultsViewElement: this.el.querySelector("[data-finder-results-view]"),
    });

    this.searchHeader = this.$(".doctor-finder-header");
    this.resultsHeader = this.$("#results-header");
    this.loadingIndicators = [];

    this.updateSearchHeaderVisibility();
    this.updateFiltersVisibility();

    // BEHAVIOUR

    jQuery(window).on("resize", debounce(this.setStickyHeader, 250));

    this.listenTo(this.collection, "sync reset", this.handleCollectionUpdate);
    this.finderAdapter = connectLegacyFinder(this.el);
    this.listenTo(this.collection, "change:filters", this.handleFiltersChange);
    this.listenTo(this.collection, "request", this.handleRequestStarted);
    this.listenTo(this.collection, "change:results", this.handleRequestCompleted);
    this.listenTo(this.collection, "request:error", this.handleRequestFailed);
    this.listenTo(this.collection, "change:selectedView", this.handleViewChanged);
    this.listenTo(this.collection, "change:selectedResult", this.handleMapSelectionChanged);
    this.handleMapSelectionChanged(this.collection.selectedResultGUID);
    this.finderAdapter.reportMapBoundsMode?.(this.shouldScopeToMapBoundsByDefault() ? "auto" : "manual");

    this.listenTo(this.collection, "request", this.loadingState);
    this.listenTo(this.collection, "sync", this.readyState);

    // Filter apps (`sp-finder-filter`) emit a NON-bubbling `change`, so the delegated
    // `change SEARCH_FILTER_SELECTOR` handler can't see it — we must bind directly
    // to each element. Binding awaits the mount-ready contract per instance:
    // `customElements.whenDefined` is tag-level and, under boot-time definitions,
    // fires before `setFilterValues` exists. A WeakSet guards double-binding.
    // A deep-link/back-forward restore can run before the element is operational,
    // so `applyInputParams` stashes the multi-value payload on the host — flushed
    // here once the element is (see `restoreMultiValueFilterApp`).
    // Scope to this view's root (`this.el`) — a global `document` query would bind
    // this instance's handler to every finder/embed's filter apps on the page, so a
    // filter change in one finder would mutate another's collection.
    this.boundFilterApps = []; // Tracks elements bound below so `remove` can detach them
    const boundFilterApps = new WeakSet();
    const bindFilterApp = (searchControl) => {
      // The deferred mount-ready continuation can fire after teardown / detach
      // (or after the element itself left the DOM); no-op then.
      if (this._torndown || !this.el?.isConnected || !searchControl.isConnected) {
        return;
      }
      this.flushPendingFilterRestore(searchControl);
      if (!boundFilterApps.has(searchControl)) {
        boundFilterApps.add(searchControl);
        this.boundFilterApps.push(searchControl);
        searchControl.addEventListener("change", this.handleSearchFilterChange);
      }
    };

    this.el.querySelectorAll(SEARCH_FILTER_SELECTOR).forEach((searchControl) => {
      if (!searchControl.matches(FINDER_FILTER_TAG)) {
        return;
      }
      DoctorFinder.Helpers.FilterAppReadiness.whenOperational(searchControl).then(bindFilterApp, (error) => {
        console.error("sp-finder-filter failed to load; leaving its filters unbound", error);
      });
    });

    if ((occlusionOptions = options.mapResultsOcclusion)) {
      return (this.resultOcclusionObserver = new IntersectionObserver(
        (entries) =>
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              return occlusionOptions.occluding();
            } else {
              return occlusionOptions.out();
            }
          }),

        { rootMargin: occlusionOptions.region },
      ));
    }
  }

  // TEARDOWN

  remove() {
    this._torndown = true;
    this.finderAdapter?.disconnect();
    this.boundFilterApps?.forEach((control) => control.removeEventListener("change", this.handleSearchFilterChange));
    this.boundFilterApps = [];

    return super.remove(...arguments);
  }

  // EVENT HANDLERS

  handleFiltersChange(event) {
    this.focusInvalidFilters();
    this.finderAdapter.publishSearch(
      this.collection.generateFetchParams({
        onlyFilters: true,
      }),
      {
        filterValues: this.collection.filters,
      },
    );
    this.updateConditionalFilters();
  }

  handleRequestStarted(url, params, generation) {
    this.finderAdapter.reportRequest({ generation, params, url });
  }

  handleRequestCompleted(results, params, url, { generation }) {
    this.completedGeneration = generation;
    this.completedMapFetchParams = cloneFinderData(params);
    this.reportResults();
  }

  // Publishes the collection's prepared payload: expired slots dropped, grid truncated, URLs decorated.
  reportResults() {
    if (this.completedGeneration === undefined) {
      return;
    }

    const results = this.collection;
    this.finderAdapter.reportCompletion({
      generation: this.completedGeneration,
      resultCount: results.resultCount,
      result: {
        ...results.originalResponse,
        results: results.toJSON(),
        appointment_grid: results.appointmentGrid,
        map_fetch_params: this.completedMapFetchParams,
      },
      view: results.selectedView,
    });
  }

  handleMapSelectionChanged(resultGUID) {
    this.finderAdapter.reportMapSelection?.(resultGUID);
  }

  handleViewChanged(view) {
    this.finderAdapter.reportView(view);
  }

  handleRequestFailed(params, url, { generation }) {
    this.finderAdapter.reportError({ generation, error: "request-failed" });
  }

  handleMobileBreakpointEnter() {
    DoctorFinder.Helpers.MAX_SLOTS = MOBILE_MAX_SLOTS;
    return this.rerender();
  }

  handleMobileBreakpointExit() {
    DoctorFinder.Helpers.MAX_SLOTS = DEFAULT_MAX_SLOTS;
    return this.rerender();
  }

  handleRemoveFilterClick(filterName) {
    if (filterName === "availability") {
      // Turn off all the availability filters if the availability filter is removed
      const availabilityFilters = ["availability", "available_times", "available_days", "available_date"];
      if (this.availabilityWidgetOwnsVisitTypes) {
        availabilityFilters.push("visit_types");
      }
      return this.removeFilters(...availabilityFilters);
    } else if (filterName === "zip") {
      // Turn off all the spatial filters if the zip filter is removed
      if (this.requiredFiltersLocked()) {
        return;
      }
      const near = this.el.querySelector('sp-finder-filter[data-filter-name="near"]');
      if (near) {
        const deactivateNear = () => near.deactivate();
        const reportNearLoadFailure = (error) =>
          console.error("sp-finder-filter failed to load; leaving Near unchanged", error);
        DoctorFinder.Helpers.FilterAppReadiness.whenOperational(near).then(deactivateNear, reportNearLoadFailure);
        return;
      }
      return this.removeFilters("zip", "lat", "lng", "distance");
    } else {
      return this.removeFilters(filterName);
    }
  }

  handleViewOptionChange(event) {
    const input = jQuery(event.target);
    return this.collection.selectView(input.val());
  }

  handleSearchFilterChange(event) {
    const input = jQuery(event.target);
    const element = input[0];
    const eventDetail = event.originalEvent?.detail ?? event.detail;
    if (input.is(FINDER_FILTER_TAG)) {
      // `sp-finder-filter` emits its value in the change detail; a detail-less native `change` bubbles
      // first while `.val()` is still stale. Read the detail and ignore the bubble so the first
      // selection isn't dropped.
      event.stopPropagation();
      if (eventDetail == null) {
        return;
      }
    }

    let filters = {};

    // The emitted value (first change-detail element). The Provider panel emits a
    // FormData of its active multi-value sub-selections; everything else flows
    // through the normal serialized path.
    const value = element?.matches(FINDER_FILTER_TAG) ? eventDetail?.[0] : undefined;
    const ownedFilterNames = element?.matches(FINDER_FILTER_TAG) ? this.ownedFilterNames(element) : [];

    if (value instanceof FormData && element.filterNames) {
      // The Provider panel emits a FormData of its active sub-selections. Clear every
      // param it owns first so a removed sub-selection is dropped from the URL, then
      // set the ones that are present.
      for (const name of ownedFilterNames) {
        filters[name] = "";
      }
      value.forEach((val, name) => {
        filters[name] = val;
      });
    } else if (ownedFilterNames.length > 1 && value == null) {
      // A cleared multi-value filter app dispatches `undefined`; every param it owns leaves the URL.
      for (const name of ownedFilterNames) {
        filters[name] = "";
      }
    } else {
      const serialized = this.getSerializedInputData(input, event);
      filters[serialized.name] = serialized.value;

      if (
        serialized.name === "insurance_plan" &&
        this.options.locationSettings?.filterOptions?.persist_insurance_plan
      ) {
        if (serialized.value) {
          window.sessionStorage.setItem(DoctorFinder.INSURANCE_PLAN_SESSION_STORAGE_KEY, serialized.value);
        } else {
          window.sessionStorage.removeItem(DoctorFinder.INSURANCE_PLAN_SESSION_STORAGE_KEY);
        }
      }
    }

    if (input.is('sp-finder-filter[data-filter-name="near"]')) {
      filters = this.nearFilterChanges(filters, eventDetail?.[1]);
    }

    return this.updateFiltersWithQueue(filters, {
      resetPage: !eventDetail?.silent,
    });
  }

  handleSkillClick() {
    return this.$("#specialty-form-group .selected_text_container").trigger("click");
  }

  handleShowPage(e) {
    e.preventDefault();
    this.setPage(jQuery(event.target).data("page"));
    return this.scrollToTopOfResults();
  }

  handleSetPerPage(e) {
    e.preventDefault();
    this.setPerPage(jQuery(event.target).data("per-page"));
    return this.scrollToTopOfResults();
  }

  handleAddFiltersClick(event) {
    event.preventDefault();
    this.addFilters(Sparkle.Utilities.paramsToObject(event.currentTarget.search));
    return this.scrollToTop();
  }

  handleResultAddFilters(_event, filters) {
    this.addFilters(filters);
    return this.scrollToTop();
  }

  handleFocusFilterClick(event) {
    event.preventDefault();
    event.stopPropagation();
    return this.focusFilterGroup(jQuery(event.currentTarget).data("filter-name"));
  }

  handleResetFiltersClick(event) {
    event.preventDefault();
    return this.removeFilters();
  }

  // Location intent belongs to the search: a list pick supersedes an entry roster even when
  // the city is unchanged; a radius-only update preserves it.
  nearFilterChanges(filters, metadata) {
    if (metadata?.locationSelected && !metadata.transientLocation) {
      metadata = { ...metadata, transientLocation: false };
    }
    this.options.onNearProvenanceChange?.(filters, metadata);
    if (filters.zip) {
      filters.bounds = null;
      if (metadata?.locationSelected) {
        if (this.collection.filters.location != null) {
          filters.location = "";
        } else {
          filters = DoctorFinder.Helpers.clearSupersededEntryScopes(filters, this.collection.filters, {
            zip: "location",
          });
        }
        if (this.collection.filters.location != null && "location" in filters) {
          filters.specialty_scope = "";
        }
      }
      this.handleMapBoundsScopingModeChange("manual");
      const location = _.pick(filters, "zip", "distance", "lat", "lng");
      if (metadata?.locationDisplayName) {
        location.locationDisplayName = metadata.locationDisplayName;
      }
      if (!metadata?.transientLocation) {
        this.persistLocation(location);
      }
    } else {
      this.clearPersistedLocation();
    }
    return filters;
  }

  persistLocation(location) {
    if (!this.options.locationSettings?.filterOptions?.persist_location) {
      return;
    }
    return window.sessionStorage.setItem(DoctorFinder.LOCATION_SESSION_STORAGE_KEY, JSON.stringify(location));
  }

  clearPersistedLocation() {
    if (!this.options.locationSettings?.filterOptions?.persist_location) {
      return;
    }
    return window.sessionStorage.removeItem(DoctorFinder.LOCATION_SESSION_STORAGE_KEY);
  }

  handleNavigateToMainClick() {
    const params = jQuery.param(_.extend({}, this.collection.filters, this.collection.options.scope));
    return (document.location.href = `${this.options.mainDoctorFinderUrl}?${params}`);
  }

  handleCollectionUpdate() {
    const paramsHash = _.extend({}, this.collection.filters, this.collection.options.scope);
    this.collection.decorateUrls({ new_patient: paramsHash["new_patient"] });

    this.collection.decorateUrls({
      search_url: (result) => {
        // Ensure that the search_url always includes a resultGUID as the URL hash (fragment)
        const search = new URL(window.location.href);
        search.hash = result.resultGUID;
        return search.href;
      },
    });
    return this.render();
  }

  handleMapBoundsScopingModeChange(mode) {
    this.mapBoundsMode = mode;
    this.storeDoctorFinderMapBoundsScopingMode(mode);
    this.finderAdapter.reportMapBoundsMode?.(mode);
  }

  // RENDERING

  render() {
    const focussedElement = document.activeElement;

    this.collection.truncateAppointmentGrid(1, !!this.isMobileSize() || this.collection.selectedView === "map");
    this.reportResults();
    this.updateViewInput();

    this.searchResultsView.render();
    this.$("#doctor-finder-search-results-container").append(this.searchResultsView.el);

    this.updateFilterCount();
    this.updateHeaderTitle();
    this.setStickyHeader();
    this.decorateFilters();
    this.$(".appointment-grid-header")
      .addClass("active")
      .toggleClass("appointment-grid-header--directory", this.isDirectoryLayout());
    this.updateErrors();
    this.updateSearchHeaderVisibility();
    this.updateSearchSummaryVisibility();
    this.updateStickyMapOffset();
    this.updateUnoccluder();

    if (focussedElement != null) {
      focussedElement.focus();
    }

    super.render(...arguments);

    return this;
  }

  // HELPERS

  // The selected visit type's wording, from whichever filter owns `visit_types`: the standalone
  // Visit Type filter's `selectedText`, or the owning multi-value filter's `selectedTexts` entry.
  // `this.availableFilterInputs` was captured before `updateFiltersVisibility` ran, so it still
  // holds a control the embed had removed; the query runs against the DOM instead.
  selectedVisitTypeText() {
    const owner = _.find(this.$(FINDER_FILTER_TAG), (element) =>
      this.ownedFilterNames(element).includes("visit_types"),
    );
    if (!owner) {
      return;
    }
    if (owner.filterNames) {
      return owner.selectedTexts?.visit_types;
    }
    return owner.selectedText;
  }

  isDirectoryLayout() {
    return this.options.resultTemplate === "directory";
  }

  shouldScopeToMapBoundsByDefault() {
    switch (this.loadDoctorFinderMapBoundsScopingMode()) {
      case "manual":
        return false;
      case "auto":
        return true;
      default:
        return this.options.scopeResultsToMapBounds;
    }
  }

  storeDoctorFinderMapBoundsScopingMode(mode) {
    return window.localStorage.setItem("doctor-finder-map-bounds-scoping-mode", JSON.stringify(mode));
  }

  loadDoctorFinderMapBoundsScopingMode() {
    return JSON.parse(window.localStorage.getItem("doctor-finder-map-bounds-scoping-mode") || "null");
  }

  decorateFilters() {
    return this.decorateFilterInputsWithFilterDescription();
  }

  // Update the filter inputs with their filter descriptions so the Filter Bar controls can create an accurate summary
  decorateFilterInputsWithFilterDescription() {
    return jQuery.each(this.availableFilterInputs, (index, filter) => {
      const description = this.collection.filterDescriptions[jQuery(filter).attr("name")];
      if (description) {
        return jQuery(filter).attr("data-filter-description", description.value);
      } else {
        return jQuery(filter).removeAttr("data-filter-description");
      }
    });
  }

  // Given an input, returns the serialized form data that input, i.e. {name, value}.
  getSerializedInputData(input, event) {
    let serialized;
    const name = input.attr("name");

    if (input.is(":checked")) {
      // Handle the case where we have multiple instances of the input. We want to use the value from the one we were given, not the dups.
      serialized = { name, value: input.val() };
    } else if (input.is(":checkbox, :radio")) {
      serialized = this.availableFilterInputs.filter(`[name='${name}']`).serializeArray()[0];
    } else if (input.is(SORT_TAG)) {
      serialized = { name, value: input[0].value };
    } else if (input.is(FINDER_FILTER_TAG)) {
      const eventDetail = event?.originalEvent?.detail ?? event?.detail;
      let value = DoctorFinder.Helpers.FilterAppValue.submitted(input[0], eventDetail?.[0]);
      if (value == null) {
        value = "";
      }
      serialized = { name, value };
    } else {
      serialized = input.serializeArray()[0];
    }

    if (!serialized) {
      serialized = { name, value: null };
    } // Ensure we return a value if the input was not part of the serialized payload, e.g. it was an unselected checkbox

    return serialized;
  }

  updateSearchHeaderVisibility() {
    if (this.options.searchHeader === false || this.collection.suppressResults) {
      this.searchHeader.hide();
      this.resultsHeader.hide();
      return;
    }

    if (this.options.mainDoctorFinderUrl) {
      this.$(".appointment-grid-header-actions").show();
      this.availableResultsControls.each((index, element) => jQuery(element).hide());
    } else if (_.isArray(this.options.searchHeader)) {
      this.availableResultsControls.each((index, element) => {
        let needle;
        if (((needle = jQuery(element).data("filter-name")), !Array.from(this.options.searchHeader).includes(needle))) {
          return jQuery(element).hide();
        }
      });
    }

    if (_.isArray(this.options.resultTypes) && !Array.from(this.options.resultTypes).includes("Physician")) {
      this.availableResultsControls.find("option[value='ratings']").remove();
    }

    if (_.isArray(this.options.filters) && !Array.from(this.options.filters).includes("near")) {
      this.availableResultsControls.find("option[value='distance']").remove();
    }

    // Add defensive check to remove the distance option if the near filter is not present
    const distanceOption = this.availableResultsControls.find("option[value='distance']");
    const nearFilterPresent = this.$('[data-filter-name="near"]').length > 0;
    if (!nearFilterPresent) {
      return distanceOption.remove();
    }
  }

  updateSearchSummaryVisibility() {
    if (!this.getTitleConfig()) {
      return this.$(".doctor-finder-header-title").hide();
    }
  }

  updateFiltersVisibility() {
    // Enable filters specified in options
    this.availableFilterControls.each((index, element) => {
      if (!this.isFilterEnabled(jQuery(element).data("filter-name"))) {
        return jQuery(element).remove();
      }
    });

    if (!this.options.openScheduling) {
      this.availableFilterControls.filter('[data-filter-name="availability"]').remove();
    }

    const bar = this.el.querySelector("sp-finder-filter-bar");

    if (bar) {
      bar.classList.toggle("--is-visible", this.options.filters !== false && Boolean(this.options.showFilters));
    }
  }

  updateViewInput() {
    return this.updateInputsFromJSON({ view: this.collection.selectedView });
  }

  // Set inputs from the params. If the param is not present, do not change it
  updateInputsFromJSON(paramsObject, nearMetadata) {
    return this.applyInputParams(paramsObject, { clearMissing: false, nearMetadata });
  }

  // Set inputs from the params. If the param is not present, clear the input
  setInputsFromJSON(paramsObject, nearMetadata) {
    return this.applyInputParams(paramsObject, { clearMissing: true, nearMetadata });
  }

  // The multi-value filter apps on the page (the Provider panel) — elements that own
  // several params and restore them through `setFilterValues` rather than a `[name]`
  // input. Detected by the server-rendered `data-filter-names` so they are recognised
  // even before the custom element mounts (the `setFilterValues` method and
  // `filterNames` property only exist post-mount).
  multiValueFilterApps() {
    return _.filter(this.availableFilterInputs.toArray(), (element) => element.hasAttribute("data-filter-names"));
  }

  // Post-mount this applies through the element's `setFilterValues`; pre-mount
  // it stashes the payload, which the mount-ready continuation in `initialize`
  // replays (see helpers/filter_app_readiness).
  restoreMultiValueFilterApp(element, values, metadata) {
    return DoctorFinder.Helpers.FilterAppReadiness.restore(element, values, metadata);
  }

  flushPendingFilterRestore(element) {
    return DoctorFinder.Helpers.FilterAppReadiness.flushPendingRestore(element);
  }

  // Shared restore: route params owned by a multi-value filter app to its
  // `setFilterValues`, and everything else to the legacy `[name]` inputs. `clearMissing`
  // mirrors setInputsFromJSON — owned params absent from the payload are reset to '' and
  // legacy inputs not present are cleared.
  applyInputParams(paramsObject, options = {}) {
    const { clearMissing } = options;
    const filterApps = this.multiValueFilterApps();

    const ownedBy = {};
    filterApps.forEach((element) =>
      this.ownedFilterNames(element).forEach((name) => {
        ownedBy[name] = element;
      }),
    );

    const restores = new Map();
    const collect = (element, name, value) => {
      const values = restores.get(element) || {};
      values[name] = value;
      restores.set(element, values);
    };

    filterApps.forEach((element) =>
      this.ownedFilterNames(element).forEach((name) => {
        if (paramsObject.hasOwnProperty(name)) {
          collect(element, name, paramsObject[name]);
        } else if (clearMissing) {
          collect(element, name, "");
        }
      }),
    );
    restores.forEach((values, element) =>
      this.restoreMultiValueFilterApp(
        element,
        values,
        element.matches('sp-finder-filter[data-filter-name="near"]') ? options.nearMetadata : undefined,
      ),
    );

    if (clearMissing) {
      this.availableInputs.each(function () {
        if (_.contains(filterApps, this)) {
          return; // Provider panel handled above
        }
        const input = jQuery(this);
        const value = paramsObject[input.attr("name")] || "";
        Sparkle.Utilities.setInputValue(input, value);
        return input.trigger("render");
      });
    } else {
      jQuery.each(paramsObject, (key, value) => {
        if (ownedBy[key]) {
          return; // already routed to a filter app
        }
        const input = jQuery(this.availableInputs).filter(`[name='${key}']`);
        Sparkle.Utilities.setInputValue(input, value);
        return input.trigger("render");
      });
    }
  }

  showMapView() {
    return this.collection.selectView("map");
  }

  navigateToDate(date) {
    if (date) {
      return this.updateFiltersWithQueue({ slots_ui_start_date: date });
    }
  }

  requiredFilterNames() {
    if (!this.requiredFiltersLocked()) {
      return [];
    }
    return ["insurance_plan", "zip", "lat", "lng", "distance"];
  }

  removeFilters(...filterNames) {
    if (!filterNames.length) {
      filterNames = this.getFilterNames();
    } // Default to remove all filters if no names are specified
    const requiredFilterNames = this.requiredFilterNames();
    filterNames = filterNames.filter((name) => !requiredFilterNames.includes(name)); // Never clear the legally required filters (see <sp-required-filters-modal>)
    if (!filterNames.length) {
      return;
    }
    const defaultDistance = this.options.locationSettings?.filterOptions?.distance?.default;

    if (filterNames.includes("zip")) {
      this.clearPersistedLocation(); // Drop the persisted location when the location filter is removed
    }

    const filters = {};
    filterNames.forEach((filterName) => {
      if (filterName === "distance" && defaultDistance) {
        return (filters[filterName] = defaultDistance);
      } else {
        return (filters[filterName] = "");
      }
    });
    if (filterNames.includes("zip")) {
      this.options.onNearProvenanceChange?.(filters, { transientLocation: false });
    }
    return this.updateFiltersWithQueue(filters, { resetPage: true });
  }

  addFilters(filters) {
    return this.updateFiltersWithQueue(filters, { resetPage: true });
  }
  updateFiltersWithQueue(filters, options = {}) {
    resetPageQueue = resetPageQueue || options.resetPage;
    jQuery.each(filters, (key, value) => updateFiltersQueue.set(key, value));

    return this.nextTick(() => {
      if (!(updateFiltersQueue.size > 0)) {
        return;
      }

      let toSet = Object.fromEntries(updateFiltersQueue);
      const toReset = resetPageQueue;
      updateFiltersQueue.clear();
      resetPageQueue = false;

      // A changed specialty supersedes the `specialty_scope` an entry link planted (e.g.
      // a location page's "who works here" search) — left in place it would silently AND
      // with the newly picked specialty and over-constrain the search to zero results
      // (sparkle-christus#303). Programmatic input restores re-emit the currently applied
      // value, so the helper's changed-value guard keeps route loads unaffected.
      const cleared = DoctorFinder.Helpers.clearSupersededEntryScopes(toSet, this.collection.filters, {
        skill: "specialty_scope",
      });

      // When that scope arrived as part of an anchored entry pair (`location` +
      // `specialty_scope` — a "who works here" search), the whole pair is superseded: the
      // specialty facet offers every specialty, not just the anchored roster, so keeping
      // the anchor would trap the new pick at zero results. A lone `location` anchor
      // (e.g. the /locations/:slug/physicians pretty URL) survives specialty refinement.
      if (
        cleared !== toSet &&
        this.collection.filters.location != null &&
        this.collection.filters.specialty_scope != null &&
        !("location" in cleared)
      ) {
        cleared.location = "";
      }
      toSet = cleared;

      const updated = this.collection.updateFilters(toSet, { resetPage: toReset });
      if (this.options.onNearProvenanceUnchanged) {
        updated.then((changed) => {
          if (!changed) {
            this.options.onNearProvenanceUnchanged();
          }
        });
      }
      return updated;
    });
  }

  getFilterNames() {
    return _.uniq(jQuery.map(this.$('[data-behaviour~="search-filter"]'), (element) => this.ownedFilterNames(element)));
  }

  // The search params a filter element owns. A multi-value filter app (the Provider
  // panel owns new_patient/name/provider_type/affiliation) advertises them in
  // server-rendered `data-filter-names`, so the registry is correct even before the
  // custom element upgrades — its `filterNames` property is only set post-upgrade,
  // while `this.availableFilters` and `getFilterNames` are both read on the initial
  // (pre-upgrade) pass. Single-value filters fall back to `name`.
  ownedFilterNames(element) {
    const declared = element.getAttribute("data-filter-names");
    if (declared) {
      return declared.split(/\s+/).filter(Boolean);
    }
    return element.filterNames || [element.getAttribute("name")];
  }

  updateFilterCount() {
    const isNonClearable = (control) => {
      const value = control.getAttribute("non-clearable");
      return value != null && value !== "false";
    };
    const activeFilterControls = _.filter(
      this.availableFilterControls,
      (control) => control.active && !isNonClearable(control),
    );
    const count = activeFilterControls.length;

    return this.$("[data-doctor-finder-behaviour~=filter-count]").text(count).toggleClass("zero", !count);
  }

  setStickyHeader() {
    const offset = this.getStickyHeaderOffset();
    Sparkle.Utilities.unstick(this.stickyHeader);
    this.stickyHeader = [];

    if (this.isMobileSize()) {
      if (this.options.stickyDateSelector !== false) {
        this.stickyHeader.push(...this.el.querySelectorAll('sp-finder-appointment-date-selector[placement="list"]'));
      }
    } else {
      this.stickyHeader.push(this.resultsHeader);
    }

    Sparkle.Utilities.sticky(this.stickyHeader, { top: offset });
    return this.updateStickyMapOffset();
  }

  updateHeaderTitle() {
    if (!this.collection.isFetched) {
      return;
    }

    if (this.options.count === false) {
      this.$('[data-doctor-finder-behaviour~="result-count"]').text("");
    } else {
      this.$('[data-doctor-finder-behaviour~="result-count"]').text(this.collection.resultCount.toLocaleString());
    }

    this.$('[data-doctor-finder-behaviour~="search-title"]').text(this.generateHeaderTitle());
    const searchContext = this.generateHeaderContext();
    this.$('[data-doctor-finder-behaviour~="search-context"]').text(searchContext).prop("hidden", !searchContext);
  }

  generateHeaderTitle() {
    const titleConfig = this.getTitleConfig();
    let searchTitle = "";
    if (_.isString(titleConfig)) {
      return (searchTitle = titleConfig);
    } else if (_.isObject(titleConfig)) {
      return (searchTitle = this.collection.resultCount === 1 ? titleConfig.singular : titleConfig.plural);
    } else if (_.isFunction(titleConfig)) {
      return (searchTitle = titleConfig(this.collection.resultCount, this.collection.filterDescriptions));
    } else if (titleConfig !== false) {
      return (searchTitle = this.defaultTitle(this.collection.resultCount, this.collection.filterDescriptions));
    }
  }

  getTitleConfig() {
    let titleConfig;
    if (this.options.showTitle === false) {
      return false;
    }

    if (this.collection.physiciansResultCount && !this.collection.locationsResultCount) {
      titleConfig = this.options.physiciansTitle;
    }
    if (this.collection.locationsResultCount && !this.collection.physiciansResultCount) {
      titleConfig = this.options.locationsTitle;
    }
    if (titleConfig == null) {
      titleConfig = this.options.title;
    }
    if (this.collection.suppressResults) {
      titleConfig = false;
    }

    return titleConfig;
  }

  defaultTitle(count, filterDescriptions) {
    let title = "";
    if (filterDescriptions.skill != null) {
      title = title.concat(`${filterDescriptions.skill.value} `);
    }
    title = title.replace(/doctors?\s*/i, " "); // Remove the word "Doctor" from the title if it is part of a specialty name since we're about to append it
    title = title.concat(count === 1 ? "Doctor" : "Doctors");
    return title;
  }

  generateHeaderContext() {
    if (this.providerSearchContext() !== SEARCH_WHO_WORKS_HERE_CONTEXT) {
      return "";
    }
    return this.collection.filterDescriptions.location?.value || "";
  }

  providerSearchContext() {
    return Sparkle.Utilities.paramsToObject(window.location.search)[PROVIDER_SEARCH_CONTEXT_PARAM];
  }

  // Prompts for a location when the results become sorted by distance without one, and stays quiet
  // while they remain that way: every filter change fires `change:filters`, so prompting on the state
  // rather than on the transition reopened the location dialog after each unrelated change.
  focusInvalidFilters() {
    const missingZip = this.sortingByDistanceWithoutZip();
    if (missingZip && !this.wasMissingZip) {
      this.focusFilter("zip");
    }
    this.wasMissingZip = missingZip;
  }

  // This updates the visibility of conditional filters based on the currently active filters and scopes
  updateConditionalFilters() {
    const activeFilters = this.getScopingFilters();
    const activeScopes = {
      ...activeFilters,
      ...this.collection.options.scope,
    };

    return jQuery.each(this.options.filterOptions, (filterName, filterOptions) => {
      const filterGroup = this.availableFilterControls.filter('[data-filter-name="' + filterName + '"]')[0];
      const { needs } = filterOptions;
      if (!filterGroup || !((needs != null ? needs.length : undefined) > 0)) {
        return;
      }

      const needsMet = needs.some((need) => activeScopes.hasOwnProperty(need));
      DoctorFinder.Helpers.FilterPrerequisite.write(filterGroup, needsMet);

      // If one of the needs is an active filter, highlight it in the UI
      const needIsAFilter = needs.some((need) => activeFilters.hasOwnProperty(need));
      return filterGroup.classList.toggle("--highlight", needIsAFilter);
    });
  }

  // Filters that affect which results are returned, not the order of results
  getScopingFilters() {
    return _.omit(this.collection.filters, "slots_ui_start_date", "page", "per_page");
  }

  launchVideoModal(name, profileUrl, videoUrl) {
    const header = '<a class="physician-profile-link" href=' + profileUrl + ">" + name + "</a>";
    return new Sparkle.VideoModal(header, videoUrl);
  }

  updateErrors() {
    // Color the address input border red if sort by distance is chosen, but no address is set
    const near = this.el.querySelector('sp-finder-filter[data-filter-name="near"]');
    const hasDistanceSortError = this.collection.errors.distance_sort_without_location != null;
    if (hasDistanceSortError) {
      this.$("#result-sort").addClass("error");
      this.focusFilter("zip");
    } else {
      this.$("#result-sort").removeClass("error");
    }
    near?.classList.toggle("error", hasDistanceSortError);
  }

  updateUnoccluder() {
    if (!this.resultOcclusionObserver) {
      return;
    }

    this.resultOcclusionObserver.disconnect();

    if (!this.isMobileSize() && this.collection.selectedView === "map") {
      return this.resultOcclusionObserver.observe(this.el);
    } else {
      return this.options.mapResultsOcclusion.out();
    }
  }

  changePage(increment) {
    return this.setPage(this.collection.page + increment);
  }

  setPage(page) {
    return this.collection.selectPage(page);
  }

  // The actions `sp-finder-result-view` runs through `sp-finder`.
  resultActions() {
    return {
      showPage: (page) => {
        this.setPage(page);
        this.scrollToTopOfResults();
      },
      showPerPage: (perPage) => {
        this.setPerPage(perPage);
        this.scrollToTopOfResults();
      },
      showDate: (date) => this.navigateToDate(date),
      selectView: (view) => this.collection.selectView(view),
      showOnMap: (resultGUID) => {
        if (resultGUID && !this.isMobileSize()) {
          this.collection.selectView("map");
        }
        if (resultGUID === this.collection.selectedResultGUID) {
          this.handleMapSelectionChanged(resultGUID);
        } else {
          this.collection.selectResult(resultGUID);
        }
      },
      addFilters: (filters) => this.handleResultAddFilters(null, filters),
      showMoreSlots: ({ focusDate, filters }) =>
        this.searchResultsView.handleShowMoreSlots(null, { focusDate, filters }),
      playVideo: ({ name, url, videoUrl }) => this.launchVideoModal(name, url, videoUrl),
      dateAvailabilityParams: (range) => this.availabilityIndicatorParams(range),
    };
  }

  // Bounds are a collection filter; the map reads presentation from the collection.
  mapActions() {
    return {
      applyMapBounds: (bounds) => {
        const normalized = normalizeMapBounds(bounds);
        if (!normalized) {
          return;
        }
        this.addFilters({ bounds: normalized });
        return this.nextTick(() => this.scrollToTopOfResults());
      },
      clearMapSelection: () => this.collection.unselectResult(),
      setMapBoundsMode: (mode) => {
        if (["auto", "manual"].includes(mode)) {
          this.handleMapBoundsScopingModeChange(mode);
        }
      },
    };
  }

  setPerPage(perPage) {
    return this.collection.selectPerPage(perPage);
  }

  loadingState() {
    if (!this.loadingStateCounter) {
      this.loadingStateCounter = 0;
    }
    this.loadingStateCounter++;

    if (this.loadingStateTimeout) {
      return this.showLoadingIndicators();
    } else {
      return (this.loadingStateTimeout = setTimeout(this.showLoadingIndicators, 500));
    }
  }

  readyState() {
    if (--this.loadingStateCounter < 1) {
      this.hideLoadingIndicators();
      clearTimeout(this.loadingStateTimeout);
      return delete this.loadingStateTimeout;
    }
  }

  showLoadingIndicators() {
    this.hideLoadingIndicators();
    // When the map is active, only overlay the results list — not the map itself
    if (this.collection.selectedView !== "map" || !this.searchResultsView.getMap()) {
      this.addLoadingIndicator("#doctor-finder-search-results-container", 500);
    }
    return this.addLoadingIndicator(".doctor-panel-list", 500);
  }

  hideLoadingIndicators() {
    jQuery.each(this.loadingIndicators, function () {
      return this.el.finish().fadeOut(250, function () {
        return jQuery(this).remove();
      });
    });

    // Clear the list of indicators
    return this.loadingIndicators.splice(0, this.loadingIndicators.length);
  }

  // One indicator per matching container, so hiding removes each of them.
  addLoadingIndicator(container, fade = 0) {
    for (const target of this.el.querySelectorAll(container)) {
      const indicator = new SparkleLoader();
      target.append(indicator.el.hide().fadeIn(fade).get(0));
      this.loadingIndicators.push(indicator);
    }
  }

  availabilityIndicatorParams(model) {
    const params = {
      after_date: model.startDate,
      before_date: Sparkle.Utilities.isoDate(moment(model.endDate).add(1, "day")),
      availability: model.onlineBookingOnly,
      available_times: model.availableTimes,
    };

    // Left unset rather than null where the facet owns the param: `_.defaults` fills only
    // undefined keys, so a null here would shadow the applied filter below.
    if (this.availabilityWidgetOwnsVisitTypes) {
      params.visit_types = model.visitTypes;
    }

    return _.defaults(params, this.collection.generateFetchParams({ onlyFilters: true }));
  }

  scrollToTopOfResults() {
    return this.scrollToTop({ element: "#pagination_anchor", offset: -this.getStickyHeaderOffset() });
  }

  mapStickyOffset() {
    let resultsHeaderTop = parseFloat(this.resultsHeader.css("top"));
    if (isNaN(resultsHeaderTop)) {
      resultsHeaderTop = this.getStickyHeaderOffset();
    }

    const resultsHeaderHeight = this.resultsHeader.outerHeight() || 0;
    return resultsHeaderTop + resultsHeaderHeight;
  }

  updateStickyMapOffset() {
    if (this.isMobileSize() || this.collection.selectedView !== "map") {
      return;
    }

    return this.searchResultsView.updateStickyMapOffset();
  }

  nextTick(fn) {
    return setTimeout(fn, 0);
  }

  sortingByDistanceWithoutZip() {
    return this.collection.filters.sort === "distance" && !this.collection.filters.zip;
  }

  // `filterName` is the param the filter's input submits.
  focusFilter(filterName) {
    const input = this.availableFilterInputs.filter(`[name='${filterName}']`)[0];
    return DoctorFinder.Helpers.FilterFocus.open(DoctorFinder.Helpers.FilterFocus.controlOf(input));
  }

  // `filterName` is the filter's `data-filter-name`.
  focusFilterGroup(filterName) {
    return DoctorFinder.Helpers.FilterFocus.open(DoctorFinder.Helpers.FilterFocus.controlNamed(this.el, filterName));
  }
};

// Restore sub-classes attached by glob-hoisted sub-files
if (__previousWS2) {
  Object.assign(DoctorFinder.Views.WizardStep2, __previousWS2);
}

function __guard__(value, transform) {
  return typeof value !== "undefined" && value !== null ? transform(value) : undefined;
}
